/*
 * UI constants & helpers for the Reminders feature. Kept thin and free of
 * JSX so they're easy to unit test once the backend lands.
 */

import moment from 'moment-timezone';

export const PRIORITY_OPTIONS = ['Urgent', 'High', 'Medium', 'Low'];

// Dark "card" styling shared by every ReactTooltip instance that renders the
// rich quick-description tooltip body (TaskTooltipContent). Soft shadow +
// rounded corners match modern productivity tools (Linear / Notion).
export const taskTooltipStyle = {
  background: '#0f172a',
  color: '#f8fafc',
  maxWidth: 420,
  padding: '12px 14px',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18), 0 2px 6px rgba(15, 23, 42, 0.12)',
  fontSize: 12,
  lineHeight: 1.5,
  zIndex: 100
};

export const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' }
];

// "My reminders" involvement filter — multi-select, OR-combined server-side.
// The requesting user is resolved from the auth token on the backend, so these
// values carry no uuid.
// Teachers carry no department of their own, so a teacher-owned reminder files under this one.
// Matched by name because the uuid differs per environment.
export const TEACHER_DEPARTMENT_NAME = 'Teacher';

export const INVOLVEMENT_OPTIONS = [
  { value: 'updated', label: 'Updated by me' },
  { value: 'assigned', label: 'Watching' },
  { value: 'created', label: 'Created by me' }
];

// The filter keys that are persisted (localStorage "last used" + saved presets).
// The due-date range (due_from/due_to) is intentionally excluded — persisting
// absolute dates would pin users to a stale window. Shared by index.jsx and
// presets.js so the two never drift.
export const PERSISTED_FILTER_KEYS = [
  'assignee_uuids',
  'involvement',
  'status',
  'priority',
  'label_uuid',
  'department_uuids'
];

/**
 * Normalise whatever the Depart filter was persisted as into the current
 * multi-select array shape.
 *
 * The filter used to be a single-select string (`department_uuid`), so a
 * localStorage payload or saved preset written before this change still carries
 * one. Migrate it in place rather than discarding it — a silently reset filter
 * is exactly the sort of invisible state that caused the original bug.
 *
 *   ['a','b']  → ['a','b']   (already migrated)
 *   'a'        → ['a']       (legacy single-select)
 *   '' | null  → []          (legacy "Any")
 */
export const normalizeDepartmentFilter = (src) => {
  if (!src || typeof src !== 'object') return [];
  if (Array.isArray(src.department_uuids)) {
    return src.department_uuids.filter((v) => typeof v === 'string' && v);
  }
  if (typeof src.department_uuid === 'string' && src.department_uuid) {
    return [src.department_uuid];
  }
  return [];
};

// The filter values that count as "off". Anything else is an active filter the
// user should be able to see at a glance — persisted filters survive across
// sessions, so a forgotten one silently hides reminders indefinitely.
const isFilterActive = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return !!value;
};

/**
 * Summarise the active persisted filters for the header chip.
 *
 * `resolveLabels` maps a filter key + value to a human string; anything it
 * can't resolve falls back to a count so the chip never renders a raw uuid.
 * Returns [{ key, label }] in PERSISTED_FILTER_KEYS order, or [] when nothing
 * is active.
 */
export const activeFilterSummary = (filters, resolveLabels = {}) => {
  if (!filters || typeof filters !== 'object') return [];
  const TITLES = {
    assignee_uuids: 'Team member',
    involvement: 'My reminders',
    status: 'Status',
    priority: 'Priority',
    label_uuid: 'Label',
    department_uuids: 'Department'
  };
  return PERSISTED_FILTER_KEYS.filter((k) => isFilterActive(filters[k])).map((k) => {
    const resolver = resolveLabels[k];
    const value = filters[k];
    let detail = null;
    if (typeof resolver === 'function') detail = resolver(value);
    if (!detail) detail = Array.isArray(value) ? `${value.length} selected` : String(value);
    return { key: k, label: `${TITLES[k] || k}: ${detail}` };
  });
};

// PRIORITY_STYLES / PRIORITY_DOT used to live here as Tailwind class strings
// for tinted pill chips, but every priority display in the feature now uses
// the <PriorityIcon /> component (which sets inline hex colours and so isn't
// affected by the project's restricted Tailwind palette). The exports were
// removed to avoid drift.

export const formatDue = (task, iana_timezone) => {
  if (!task?.due_at) return '—';
  const tz = iana_timezone || moment.tz.guess();
  const m = moment.tz(task.due_at, tz);
  if (!m.isValid()) return '—';
  if (task.has_time) return m.format('ddd, MMM D · h:mm A');
  return m.format('ddd, MMM D');
};

/**
 * Human-friendly distance from now:
 *   < 60 min  → "45 min left"  / "10 min ago"
 *   < 24 hr   → "In 3 hours"   / "3 hours ago"
 *   else      → "In 2 days"    / "2 days ago"
 *
 * For all-day tasks we round to whole days against the user's local day.
 */
export const formatRelativeDue = (task) => {
  if (!task?.due_at) return '—';
  const now = moment();
  const due = moment(task.due_at);
  if (!due.isValid()) return '—';

  if (!task.has_time) {
    const today = now.clone().startOf('day');
    const dueDay = due.clone().startOf('day');
    const dayDiff = dueDay.diff(today, 'days');
    if (dayDiff === 0) return 'Today';
    if (dayDiff === 1) return 'Tomorrow';
    if (dayDiff === -1) return 'Yesterday';
    if (dayDiff > 0) return `In ${dayDiff} days`;
    return `${Math.abs(dayDiff)} days ago`;
  }

  const diffMin = due.diff(now, 'minutes');
  if (diffMin === 0) return 'Due now';

  const isPast = diffMin < 0;
  const abs = Math.abs(diffMin);

  if (abs < 60) {
    return isPast ? `${abs} min ago` : `${abs} min left`;
  }
  if (abs < 60 * 24) {
    const hrs = Math.round(abs / 60);
    return isPast
      ? `${hrs} hour${hrs === 1 ? '' : 's'} ago`
      : `In ${hrs} hour${hrs === 1 ? '' : 's'}`;
  }
  const days = Math.round(abs / (60 * 24));
  return isPast
    ? `${days} day${days === 1 ? '' : 's'} ago`
    : `In ${days} day${days === 1 ? '' : 's'}`;
};

export const isPastDue = (task) => {
  if (!task?.due_at) return false;
  if (task.status === 'completed') return false;
  if (task.has_time) return moment(task.due_at).isBefore(moment());
  return moment(task.due_at).endOf('day').isBefore(moment());
};

/**
 * Pending, not yet past, due within the next 60 minutes.
 * Triggers the "green" treatment on the Due column — needs attention now.
 */
export const isDueWithinHour = (task) => {
  if (!task?.due_at) return false;
  if (task.status === 'completed') return false;
  if (isPastDue(task)) return false;
  if (!task.has_time) return false; // all-day tasks have no minute precision
  const due = moment(task.due_at);
  if (!due.isValid()) return false;
  return due.diff(moment(), 'minutes', true) <= 60;
};

/**
 * Pending, not yet past, and within the next 24 hours (or today/tomorrow for
 * all-day tasks). Triggers the "blue" treatment on the Due column.
 */
export const isDueSoon = (task) => {
  if (!task?.due_at) return false;
  if (task.status === 'completed') return false;
  if (isPastDue(task)) return false;
  const now = moment();
  if (task.has_time) {
    const due = moment(task.due_at);
    if (!due.isValid()) return false;
    return due.diff(now, 'hours', true) <= 24;
  }
  const today = now.clone().startOf('day');
  const dueDay = moment(task.due_at).startOf('day');
  if (!dueDay.isValid()) return false;
  return dueDay.diff(today, 'days') <= 1;
};

export const splitTasksUpcomingPast = (tasks) => {
  const upcoming = [];
  const past = [];
  tasks.forEach((t) => {
    // Reuse isPastDue so date-only tasks use end-of-day, keeping "today"
    // all-day reminders in Upcoming until the day rolls over.
    if (t.status === 'completed' || isPastDue(t)) past.push(t);
    else upcoming.push(t);
  });
  upcoming.sort((a, b) => moment(a.due_at).valueOf() - moment(b.due_at).valueOf());
  past.sort((a, b) => moment(b.due_at).valueOf() - moment(a.due_at).valueOf());
  return { upcoming, past };
};

// Label/department pill fills are theme-independent (API-supplied pastels), so pick the
// pill's text color from its own luminance instead of a theme token that flips with dark mode.
export const readableTextOn = (bg) => {
  if (!bg || typeof bg !== 'string') return '#1a1b1e';
  let h = bg.trim().replace('#', '');
  if (h.length === 3)
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return '#1a1b1e';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  // Perceived luminance (0–1); light fills get dark text, dark fills get white.
  // Threshold biased slightly below mid so mid-tone fills get dark text (safer contrast).
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55 ? '#1a1b1e' : '#ffffff';
};
